import debounce from 'lodash.debounce';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import ImprovedSelect from './ImprovedSelect';
import { useFormContext } from 'react-hook-form';
import { API_BASE_URL } from '../../../api';

const InputMedicalService = ({
  name,
  handleChange,
  validationRules = {},
  allowCustomValue = false,
  className,
}) => {
  const [options, setOptions]   = useState([]);
  const [page, setPage]         = useState(0);
  const [hasMore, setHasMore]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [keyword, setKeyword]   = useState('');
  const inputRef   = useRef(null);
  const loadingRef = useRef(false);
  const { getValues } = useFormContext();

  const fetchServices = useCallback(async (pageToLoad, kw) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/v1/MedicalServices`, {
        withCredentials: true,
        params: { page: pageToLoad, size: 20, keyword: kw },
      });
      const formatted = (data.content || []).map(s => ({ value: s.id, label: s.name }));
      setOptions(prev => pageToLoad === 0 ? formatted : [...prev, ...formatted]);
      setHasMore(!data.last);
      setPage(pageToLoad + 1);
    } catch (err) {
      console.error('Failed to fetch medical services', err);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  // Charge la 1ère page au montage
  useEffect(() => {
    fetchServices(0, '');
  }, [fetchServices]);

  // Pré-sélectionne la valeur si édition
  useEffect(() => {
    const elementId = getValues(name);
    if (!elementId) return;
    axios.get(`${API_BASE_URL}/api/v1/medicalService/${elementId}`, { withCredentials: true })
      .then(({ data }) => {
        if (!data || !inputRef.current) return;
        const opt = { value: data.id, label: data.name };
        inputRef.current.setExternalValue(opt);
        setOptions(prev => prev.some(p => p.value === opt.value) ? prev : [opt, ...prev]);
      })
      .catch(err => console.error('Erreur fetch medical service by id:', err));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSearch = useMemo(() => debounce((kw) => {
    setKeyword(kw);
    fetchServices(0, kw);
  }, 300), [fetchServices]);

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleLoadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      fetchServices(page, keyword);
    }
  }, [hasMore, page, keyword, fetchServices]);

  return (
    <ImprovedSelect
      ref={inputRef}
      name={name}
      multiple={false}
      options={options}
      onChange={handleChange}
      onInputChange={debouncedSearch}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      validationRules={validationRules}
      allowCustomValue={allowCustomValue}
      loading={loading}
      className={className}
    />
  );
};

export default InputMedicalService;
